import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { 
  Volume2, 
  VolumeX, 
  Maximize, 
  Minimize, 
  RotateCw,
  AlertCircle
} from 'lucide-react';

/**
 * TimelinePlayer - Advanced video player that follows timeline composition
 * 
 * This player renders the assembled timeline by switching between clips
 * based on the current playback time, providing seamless timeline playback.
 */
const TimelinePlayer = ({
  clips = [],
  currentTime = 0,
  duration = 0,
  isPlaying = false,
  onTimeUpdate,
  onLoadedMetadata,
  onError,
  aspectRatio = '16:9',
  className = '',
  autoPlay = false
}) => {
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preloadedVideos, setPreloadedVideos] = useState(new Map());
  
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const currentVideoRef = useRef(null);
  const timeUpdateIntervalRef = useRef(null);

  // Calculate which clip should be playing at current time
  const activeClip = useMemo(() => {
    return clips.find(clip => 
      currentTime >= clip.startTime && currentTime < clip.endTime
    );
  }, [clips, currentTime]);

  // Calculate time offset within the active clip
  const clipTime = useMemo(() => {
    if (!activeClip) return 0;
    return currentTime - activeClip.startTime;
  }, [activeClip, currentTime]);

  // Preload video elements for smooth transitions
  useEffect(() => {
    const videoMap = new Map();
    
    clips.forEach(clip => {
      if (clip.url && !preloadedVideos.has(clip.id)) {
        const video = document.createElement('video');
        video.src = clip.url;
        video.preload = 'metadata';
        video.muted = true; // Mute preloaded videos
        video.style.display = 'none';
        
        video.addEventListener('loadedmetadata', () => {
          videoMap.set(clip.id, video);
          setPreloadedVideos(new Map(videoMap));
        });
        
        video.addEventListener('error', (e) => {
          console.error(`Failed to preload clip ${clip.id}:`, e);
        });
        
        // Add to DOM for preloading
        document.body.appendChild(video);
      } else if (preloadedVideos.has(clip.id)) {
        videoMap.set(clip.id, preloadedVideos.get(clip.id));
      }
    });

    return () => {
      // Cleanup old preloaded videos not in current clips
      preloadedVideos.forEach((video, clipId) => {
        if (!clips.find(clip => clip.id === clipId)) {
          video.remove();
        }
      });
    };
  }, [clips]);

  // Update the main video element when active clip changes
  useEffect(() => {
    if (!activeClip || !playerRef.current) return;

    const video = playerRef.current;
    const preloadedVideo = preloadedVideos.get(activeClip.id);

    if (preloadedVideo) {
      // Copy properties from preloaded video
      if (video.src !== preloadedVideo.src) {
        video.src = preloadedVideo.src;
        video.load();
      }
    } else if (video.src !== activeClip.url) {
      video.src = activeClip.url;
      video.load();
    }

    const handleLoadedMetadata = () => {
      video.currentTime = clipTime;
      onLoadedMetadata?.({
        duration: video.duration,
        clip: activeClip
      });
    };

    const handleError = (e) => {
      setError(`Failed to load clip: ${activeClip.name}`);
      onError?.(e, activeClip);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('error', handleError);
    };
  }, [activeClip, preloadedVideos, clipTime, onLoadedMetadata, onError]);

  // Sync video time with timeline
  useEffect(() => {
    if (!playerRef.current || !activeClip) return;

    const video = playerRef.current;
    const targetTime = clipTime;
    
    // Only seek if there's a significant difference to avoid constant seeking
    if (Math.abs(video.currentTime - targetTime) > 0.1) {
      video.currentTime = targetTime;
    }
  }, [activeClip, clipTime]);

  // Handle play/pause state
  useEffect(() => {
    if (!playerRef.current) return;

    const video = playerRef.current;
    
    if (isPlaying && activeClip) {
      video.play().catch(err => {
        console.error('Failed to play video:', err);
        setError('Failed to play video');
      });
    } else {
      video.pause();
    }
  }, [isPlaying, activeClip]);

  // Handle volume changes
  useEffect(() => {
    if (!playerRef.current) return;
    
    const video = playerRef.current;
    video.volume = isMuted ? 0 : volume;
  }, [volume, isMuted]);

  // Time update handling
  useEffect(() => {
    if (!playerRef.current) return;

    const video = playerRef.current;
    
    const handleTimeUpdate = () => {
      if (activeClip && onTimeUpdate) {
        const timelineTime = activeClip.startTime + video.currentTime;
        onTimeUpdate(timelineTime);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [activeClip, onTimeUpdate]);

  // Fullscreen handling
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }, [isFullscreen]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Calculate aspect ratio styles
  const aspectRatioStyle = useMemo(() => {
    const [width, height] = aspectRatio.split(':').map(Number);
    return {
      aspectRatio: `${width} / ${height}`,
      maxWidth: '100%',
      maxHeight: '70vh'
    };
  }, [aspectRatio]);

  const clearError = () => setError(null);

  return (
    <div 
      ref={containerRef}
      className={`relative bg-black rounded-lg overflow-hidden ${className} ${
        isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''
      }`}
      style={isFullscreen ? {} : aspectRatioStyle}
    >
      {/* Video Element */}
      <video
        ref={playerRef}
        className="w-full h-full object-contain"
        playsInline
        preload="metadata"
        onLoadStart={() => setIsLoading(true)}
        onLoadedData={() => setIsLoading(false)}
        onWaiting={() => setIsLoading(true)}
        onCanPlay={() => setIsLoading(false)}
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="flex items-center space-x-2 text-white">
            <RotateCw className="w-5 h-5 animate-spin" />
            <span>Loading...</span>
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {error && (
        <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
          <div className="bg-red-600 text-white p-4 rounded-lg flex items-center space-x-2 max-w-md">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-medium">Playback Error</p>
              <p className="text-sm opacity-90">{error}</p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearError}
                className="mt-2 text-white border-white hover:bg-white hover:text-red-600"
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* No Active Clip Overlay */}
      {!activeClip && clips.length > 0 && (
        <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="text-6xl mb-4">⏸</div>
            <p className="text-lg font-medium">No clip at current time</p>
            <p className="text-sm opacity-75">
              Move the playhead to a clip on the timeline
            </p>
          </div>
        </div>
      )}

      {/* Empty Timeline Overlay */}
      {clips.length === 0 && (
        <div className="absolute inset-0 bg-zinc-900 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <div className="text-6xl mb-4">🎬</div>
            <p className="text-lg font-medium">Timeline is empty</p>
            <p className="text-sm opacity-75">
              Add clips to the timeline to start editing
            </p>
          </div>
        </div>
      )}

      {/* Player Controls Overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
        <div className="flex items-center justify-between">
          {/* Left Controls */}
          <div className="flex items-center space-x-2">
            {/* Volume Control */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMuted(!isMuted)}
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </Button>
            
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={(e) => {
                const newVolume = parseFloat(e.target.value);
                setVolume(newVolume);
                if (newVolume > 0 && isMuted) {
                  setIsMuted(false);
                }
              }}
              className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Center Info */}
          <div className="text-center text-white">
            {activeClip && (
              <div className="text-sm">
                <div className="font-medium">{activeClip.name}</div>
                <div className="text-xs opacity-75">
                  Clip {clips.findIndex(c => c.id === activeClip.id) + 1} of {clips.length}
                </div>
              </div>
            )}
          </div>

          {/* Right Controls */}
          <div className="flex items-center space-x-2">
            {/* Aspect Ratio Indicator */}
            <div className="text-xs text-gray-300 px-2 py-1 bg-black bg-opacity-50 rounded">
              {aspectRatio}
            </div>

            {/* Fullscreen Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFullscreen}
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Clip Transition Indicator */}
      {activeClip && (
        <div className="absolute top-4 left-4 bg-black bg-opacity-75 text-white px-3 py-1 rounded text-sm">
          <span className="opacity-75">Playing:</span> {activeClip.name}
        </div>
      )}
    </div>
  );
};

export default TimelinePlayer;